import { SourceMediaTypes } from 'features/source-media-tracks/enums';

export type DeviceId = string | undefined;
export type MediaStreamFactory = () => Promise<MediaStreamTrack>;

export class SourceMediaTrackUnavailableError extends Error {
  constructor() {
    super('No media track is currently available');
    this.name = 'SourceMediaTrackUnavailableError';
  }
}

export const isSourceMediaTrackUnavailableError = (
  error: unknown
): error is SourceMediaTrackUnavailableError =>
  error instanceof SourceMediaTrackUnavailableError;

export interface MediaTrackEntry {
  deviceId?: DeviceId;
  monitoredTrackId?: string;
  track?: MediaStreamTrack;
  trackFactory: MediaStreamFactory;
  isTrackUnavailable: boolean;
}

export interface SourceMediaTrackDiagnostics {
  deviceId?: DeviceId;
  monitoredTrackId?: string;
  isTrackUnavailable: boolean;
  hasTrack: boolean;
  isLocalType: boolean;
  trackDetails?: {
    id: string;
    kind: MediaStreamTrack['kind'];
    label: string;
    readyState: MediaStreamTrack['readyState'];
    enabled: boolean;
    muted: boolean;
  };
}

export class TrackRegistry {
  private readonly mediaTracks: Record<SourceMediaTypes, MediaTrackEntry>;

  constructor(
    private readonly getUserMedia: (
      constraints?: MediaStreamConstraints
    ) => Promise<MediaStream>
  ) {
    this.mediaTracks = TrackRegistry.createDefaultStreams();
  }

  public getEntries(): Record<SourceMediaTypes, MediaTrackEntry> {
    return this.mediaTracks;
  }

  public getEntry(mediaType: SourceMediaTypes): MediaTrackEntry {
    return this.mediaTracks[mediaType];
  }

  public getTrack(mediaType: SourceMediaTypes): MediaStreamTrack | undefined {
    return this.mediaTracks[mediaType].track;
  }

  public setTrack(
    mediaType: SourceMediaTypes,
    track: MediaStreamTrack | undefined
  ): void {
    const entry = this.mediaTracks[mediaType];
    entry.track = track;
    entry.isTrackUnavailable = track === undefined;
  }

  public clearTrack(mediaType: SourceMediaTypes): void {
    const entry = this.mediaTracks[mediaType];
    entry.track = undefined;
    entry.monitoredTrackId = undefined;
  }

  public getMonitoredTrackId(mediaType: SourceMediaTypes): string | undefined {
    return this.mediaTracks[mediaType].monitoredTrackId;
  }

  public setMonitoredTrackId(
    mediaType: SourceMediaTypes,
    monitoredTrackId: string | undefined
  ): void {
    this.mediaTracks[mediaType].monitoredTrackId = monitoredTrackId;
  }

  public isTrackAvailable(mediaType: SourceMediaTypes): boolean {
    const entry = this.mediaTracks[mediaType];
    if (entry.track) {
      return true;
    }

    return !entry.isTrackUnavailable;
  }

  public replaceRemoteTrack(
    mediaType: SourceMediaTypes,
    deviceId: DeviceId,
    track: MediaStreamTrack | undefined
  ): void {
    this.mediaTracks[mediaType] = TrackRegistry.createValueStreamEntry(
      deviceId,
      track
    );
  }

  public replaceLocalDevice(
    mediaType: SourceMediaTypes,
    deviceId: DeviceId,
    constraints: MediaStreamConstraints | undefined
  ): void {
    const oldEntry = this.mediaTracks[mediaType];

    if (oldEntry.track?.readyState === 'live') {
      console.log(
        `Stopping old ${mediaType} track (device changed from ${oldEntry.deviceId} to ${deviceId})`
      );
      oldEntry.monitoredTrackId = undefined;
      oldEntry.track.stop();
    }

    this.mediaTracks[mediaType] = constraints
      ? this.createLocalDeviceStreamEntry(deviceId, constraints)
      : TrackRegistry.createValueStreamEntry(undefined, undefined);
  }

  public getDiagnostics(): Record<
    SourceMediaTypes,
    SourceMediaTrackDiagnostics
  > {
    const diagnosticsEntries = Object.entries(this.mediaTracks).map(
      ([sourceMediaType, entry]) => {
        const trackDetails = entry.track
          ? {
              id: entry.track.id,
              kind: entry.track.kind,
              label: entry.track.label,
              readyState: entry.track.readyState,
              enabled: entry.track.enabled,
              muted: entry.track.muted,
            }
          : undefined;

        return [
          sourceMediaType as SourceMediaTypes,
          {
            deviceId: entry.deviceId,
            monitoredTrackId: entry.monitoredTrackId,
            isTrackUnavailable: entry.isTrackUnavailable,
            hasTrack: Boolean(entry.track),
            isLocalType: TrackRegistry.isLocalMediaType(
              sourceMediaType as SourceMediaTypes
            ),
            trackDetails,
          },
        ];
      }
    );

    return Object.fromEntries(diagnosticsEntries) as Record<
      SourceMediaTypes,
      SourceMediaTrackDiagnostics
    >;
  }

  public static isLocalMediaType(mediaType: SourceMediaTypes): boolean {
    return [
      SourceMediaTypes.INTERP_VIDEO,
      SourceMediaTypes.INTERP_AUDIO,
    ].includes(mediaType);
  }

  private static createDefaultStreams(): Record<
    SourceMediaTypes,
    MediaTrackEntry
  > {
    const streams: Partial<Record<SourceMediaTypes, MediaTrackEntry>> = {};

    for (const key of Object.values(SourceMediaTypes)) {
      streams[key] = TrackRegistry.createValueStreamEntry(undefined, undefined);
    }

    return streams as Record<SourceMediaTypes, MediaTrackEntry>;
  }

  private static createValueStreamEntry(
    deviceId: DeviceId,
    track: MediaStreamTrack | undefined
  ): MediaTrackEntry {
    const entry: MediaTrackEntry = {
      deviceId,
      track,
      trackFactory: () =>
        entry.track
          ? Promise.resolve(entry.track)
          : Promise.reject(new SourceMediaTrackUnavailableError()),
      isTrackUnavailable: track === undefined,
    };
    return entry;
  }

  private createLocalDeviceStreamEntry(
    deviceId: DeviceId,
    constraints: MediaStreamConstraints
  ): MediaTrackEntry {
    const entry: MediaTrackEntry = {
      deviceId,
      monitoredTrackId: undefined,
      track: undefined,
      isTrackUnavailable: false,
      trackFactory: async () => {
        try {
          const stream = await this.getUserMedia(constraints);
          const track = stream.getTracks()[0];

          TrackRegistry.markAsManaged(track);
          entry.isTrackUnavailable = false;

          return track;
        } catch (error) {
          entry.isTrackUnavailable = true;
          throw error;
        }
      },
    };

    return entry;
  }

  private static markAsManaged(track: MediaStreamTrack): void {
    Object.defineProperty(track, 'mercuryManaged', {
      value: true,
      configurable: false,
      enumerable: false,
      writable: false,
    });
  }
}
